import type { FC } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { AlertTriangle } from 'lucide-react';

interface CancelConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
}

/**
 * Confirmation dialog for canceling registration wizard
 * Shows warning before discarding cart
 */
export const CancelConfirmDialog: FC<CancelConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title = 'Cancel Registration?',
  description = 'Are you sure you want to cancel? Your selections will be saved, but you can also discard them.',
  confirmText = 'Discard & Exit',
  cancelText = 'Continue Registration',
}) => {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape key should close dialog (default behavior)
    // Enter key on cancel button should close dialog
    if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="backdrop-blur-md bg-turquoise-800/95 border-2 border-orange-500"
        onKeyDown={handleKeyDown}
        aria-describedby="dialog-description"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/20" aria-hidden="true">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            </div>
            <DialogTitle className="text-white">{title}</DialogTitle>
          </div>
          <DialogDescription id="dialog-description" className="pt-2 text-orange-200/90">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-orange-500 text-orange-200 hover:bg-orange-500/20"
            aria-label={cancelText}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleConfirm}
            className="bg-orange-500 text-white hover:bg-orange-600"
            aria-label={confirmText}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface RemoveItemConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  itemName: string;
  itemType: 'tournament' | 'addon' | 'item';
}

/**
 * Confirmation dialog for removing items from cart
 */
export const RemoveItemConfirmDialog: FC<RemoveItemConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  itemName,
  itemType,
}) => {
  const typeLabel = itemType === 'tournament' ? 'tournament' : itemType === 'addon' ? 'add-on' : 'item';

  return (
    <CancelConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title={`Remove ${typeLabel}?`}
      description={`Are you sure you want to remove "${itemName}" from your cart?`}
      confirmText="Remove"
      cancelText="Keep in Cart"
    />
  );
};
